import { ref, onMounted, onUnmounted } from 'vue'

// Base dimensions for scale calculations (matches vue-stripe-menu)
const BASE_WIDTH = 380
const BASE_HEIGHT = 400

export const useDropdownMenu = (options = {}) => {
  const {
    handler = 'hover',
    align = 'center',
    screenOffset = 10,
    dropdownOffset = 0,
    transitionTimeout = 250
  } = options

  // State
  const activeDropdown = ref(null)
  const activeDropdownItem = ref(null)
  const elementsWithDropdown = ref([])
  const dropdownContainerItems = ref([])
  const isDragging = ref(false)
  const lastWindowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)

  // Refs for DOM elements
  const rootEl = ref(null)
  const linkContainerEl = ref(null)
  const dropdownContainerEl = ref(null)
  const backgroundEl = ref(null)
  const backgroundAltEl = ref(null)
  const arrowEl = ref(null)

  // Timeouts
  let closeDropdownTimeout = null
  let enableTransitionTimeout = null
  let disableTransitionTimeout = null

  // Pointer events detection (determined on mount)
  let pointerEvent = {
    end: 'pointerup',
    enter: 'pointerenter',
    leave: 'pointerleave'
  }

  /**
   * Calculate and apply dropdown positioning and sizing
   */
  const resizeDropdown = () => {
    if (!activeDropdownItem.value || !rootEl.value || !activeDropdown.value) {
      return
    }

    const bodyWidth = document.documentElement.offsetWidth
    const rootRect = rootEl.value.getBoundingClientRect()
    const rect = activeDropdown.value.getBoundingClientRect()

    let { offsetHeight, offsetWidth } = activeDropdownItem.value.content

    // Find the beginning position of the menu item
    const startPosition = rect.left - rootRect.left

    // Calculate position based on alignment
    let position = null
    const itemAlign = activeDropdownItem.value.align || align

    switch (itemAlign) {
      case 'left':
        position = startPosition
        break
      case 'right':
        position = startPosition - offsetWidth + rect.width
        break
      default: // center
        position = startPosition - offsetWidth / 2 + rect.width / 2
    }

    // Don't let it go off the left side of the screen
    if (position + rootRect.left < screenOffset) {
      position = screenOffset - rootRect.left
    }

    // Check the right side of the screen
    const rightOffset = position + rootRect.left + offsetWidth
    if (rightOffset > bodyWidth - screenOffset) {
      position -= rightOffset - bodyWidth + screenOffset

      // Recheck the left side
      if (position < screenOffset - rootRect.left) {
        position = screenOffset - rootRect.left
        offsetWidth = bodyWidth - screenOffset * 2
      }
    }

    // Round to avoid font blurring
    position = Math.round(position)

    const dropdownOffsetValue = dropdownOffset + activeDropdown.value.offsetTop
    const ratioWidth = offsetWidth / BASE_WIDTH
    const ratioHeight = offsetHeight / BASE_HEIGHT

    // Activate transition
    clearDisableTransitionTimeout()
    startEnableTransitionTimeout()

    // Apply transforms
    dropdownContainerEl.value.style.transform = `translate(${position}px, ${dropdownOffsetValue}px)`
    dropdownContainerEl.value.style.width = `${offsetWidth}px`
    dropdownContainerEl.value.style.height = `${offsetHeight}px`

    arrowEl.value.style.transform = `translate(${startPosition + rect.width / 2}px, ${dropdownOffsetValue}px) rotate(45deg)`
    backgroundEl.value.style.transform = `translate(${position}px, ${dropdownOffsetValue}px) scaleX(${ratioWidth}) scaleY(${ratioHeight})`
    backgroundAltEl.value.style.transform = `translateY(${activeDropdownItem.value.content.firstElementChild.offsetHeight / ratioHeight}px)`
  }

  /**
   * Open a dropdown
   */
  const openDropdown = (el) => {
    if (activeDropdown.value === el) {
      return
    }

    activeDropdown.value = el
    el.setAttribute('aria-expanded', 'true')

    rootEl.value?.classList.add('uia-overlay-active', 'uia-dropdown-active')

    // Update active states
    elementsWithDropdown.value.forEach((element) => element.classList.remove('uia-active'))
    el.classList.add('uia-active')

    const activeDataDropdown = el.getAttribute('data-dropdown')
    let direction = 'uia-left'

    // Update dropdown sections
    dropdownContainerItems.value.forEach((item) => {
      item.el.classList.remove('uia-active', 'uia-left', 'uia-right')

      if (item.name === activeDataDropdown) {
        item.el.setAttribute('aria-hidden', 'false')
        item.el.removeAttribute('inert')
        item.el.classList.add('uia-active')
        direction = 'uia-right'
        activeDropdownItem.value = item
      } else {
        item.el.setAttribute('aria-hidden', 'true')
        item.el.setAttribute('inert', '')
        item.el.classList.add(direction)
      }
    })

    resizeDropdown()
  }

  /**
   * Close the active dropdown
   */
  const closeDropdown = () => {
    if (!activeDropdown.value) {
      return
    }

    // Store reference before clearing
    const dropdownItemToClose = activeDropdownItem.value
    const dropdownToClose = activeDropdown.value

    elementsWithDropdown.value.forEach((el) => el.classList.remove('uia-active'))

    if (dropdownItemToClose?.el) {
      // Remove focus from any focused element inside the dropdown before hiding
      try {
        const focusedElement = dropdownItemToClose.el.querySelector(':focus')
        if (focusedElement) {
          focusedElement.blur()
        }
        dropdownItemToClose.el.setAttribute('aria-hidden', 'true')
        dropdownItemToClose.el.setAttribute('inert', '')
      } catch (e) {
        // Element may have been removed from DOM
        console.warn('Error setting aria-hidden:', e)
      }
    }

    clearEnableTransitionTimeout()
    startDisableTransitionTimeout()

    rootEl.value?.classList.remove('uia-overlay-active', 'uia-dropdown-active')

    if (dropdownToClose) {
      dropdownToClose.setAttribute('aria-expanded', 'false')
    }

    activeDropdownItem.value = null
    activeDropdown.value = null
  }

  /**
   * Toggle dropdown open/close
   */
  const toggleDropdown = (el) => {
    if (activeDropdown.value === el) {
      closeDropdown()
    } else {
      openDropdown(el)
    }
  }

  /**
   * Timeout helpers
   */
  const startCloseDropdownTimeout = () => {
    closeDropdownTimeout = setTimeout(() => closeDropdown(), 50)
  }

  const clearCloseDropdownTimeout = () => {
    clearTimeout(closeDropdownTimeout)
  }

  const startEnableTransitionTimeout = () => {
    enableTransitionTimeout = setTimeout(() => rootEl.value?.classList.remove('uia-no-transition'), 50)
  }

  const clearEnableTransitionTimeout = () => {
    clearTimeout(enableTransitionTimeout)
  }

  const startDisableTransitionTimeout = () => {
    disableTransitionTimeout = setTimeout(() => rootEl.value?.classList.add('uia-no-transition'), transitionTimeout)
  }

  const clearDisableTransitionTimeout = () => {
    clearTimeout(disableTransitionTimeout)
  }

  /**
   * Register event listeners on dropdown trigger elements
   */
  const registerDropdownElementsEvents = (force = false) => {
    elementsWithDropdown.value.forEach((el) => {
      // Skip if already registered
      if (el._uiaMenu && !force) {
        return
      }

      // Remove existing handlers
      if (el._uiaMenuHandlers) {
        Object.entries(el._uiaMenuHandlers).forEach(([eventName, fn]) => {
          el.removeEventListener(eventName, fn)
        })
      }

      // Setup handlers based on interaction mode
      if (handler === 'hover') {
        el._uiaMenuHandlers = {
          focusin: () => {
            clearCloseDropdownTimeout()
            openDropdown(el)
          },
          [pointerEvent.enter]: (evt) => {
            if (!evt.pointerType || evt.pointerType !== 'touch') {
              clearCloseDropdownTimeout()
              openDropdown(el)
            }
          },
          [pointerEvent.leave]: (evt) => {
            if (!evt.pointerType || evt.pointerType !== 'touch') {
              startCloseDropdownTimeout()
            }
          }
        }
      } else {
        el._uiaMenuHandlers = {}
      }

      // Always handle click/tap
      el._uiaMenuHandlers[pointerEvent.end] = (evt) => {
        evt.preventDefault()
        evt.stopPropagation()
        toggleDropdown(el)
      }

      // Register all handlers
      Object.entries(el._uiaMenuHandlers).forEach(([eventName, fn]) => {
        el.addEventListener(eventName, fn)
      })

      el._uiaMenu = true
    })
  }

  /**
   * Register event listeners on dropdown container
   */
  const registerDropdownContainerEvents = (force = false) => {
    const el = dropdownContainerEl.value
    if (!el) return

    // Skip if already registered
    if (el._uiaMenu && !force) {
      return
    }

    // Remove existing handlers
    if (el._uiaMenuHandlers) {
      Object.entries(el._uiaMenuHandlers).forEach(([eventName, fn]) => {
        el.removeEventListener(eventName, fn)
      })
    }

    // Setup handlers based on interaction mode
    if (handler === 'hover') {
      el._uiaMenuHandlers = {
        [pointerEvent.enter]: (evt) => {
          if (!evt.pointerType || evt.pointerType !== 'touch') {
            clearCloseDropdownTimeout()
          }
        },
        [pointerEvent.leave]: (evt) => {
          if (!evt.pointerType || evt.pointerType !== 'touch') {
            startCloseDropdownTimeout()
          }
        }
      }
    } else {
      el._uiaMenuHandlers = {}
    }

    // Always stop propagation on click
    el._uiaMenuHandlers[pointerEvent.end] = (evt) => {
      evt.stopPropagation()
    }

    // Register all handlers
    Object.entries(el._uiaMenuHandlers).forEach(([eventName, fn]) => {
      el.addEventListener(eventName, fn)
    })

    el._uiaMenu = true
  }

  /**
   * Update data elements from DOM
   */
  const updateDataElements = () => {
    if (!linkContainerEl.value || !dropdownContainerEl.value) return

    // The linkContainerEl is the <ul>, we need to find the <li> with class uia-link-container
    // and then get its children which are the actual menu buttons
    const linkContainer = linkContainerEl.value.querySelector('.uia-link-container')
    if (!linkContainer) return

    elementsWithDropdown.value = Array.from(linkContainer.children).filter(
      (el) => el.classList.contains('uia-has-dropdown') || el.hasAttribute('data-dropdown')
    )

    dropdownContainerItems.value = Array.from(dropdownContainerEl.value.children).map((el) => ({
      el,
      name: el.getAttribute('data-dropdown'),
      align: el.getAttribute('data-align'),
      content: el.firstElementChild
    }))

    // Initialize aria-hidden and inert for all dropdown sections
    dropdownContainerItems.value.forEach((item) => {
      item.el.setAttribute('aria-hidden', 'true')
      item.el.setAttribute('inert', '')
    })
  }

  /**
   * Clear all inline styles
   */
  const clearAllStyles = () => {
    dropdownContainerEl.value?.removeAttribute('style')
    arrowEl.value?.removeAttribute('style')
    backgroundEl.value?.removeAttribute('style')
    backgroundAltEl.value?.removeAttribute('style')
  }

  /**
   * Global event handlers
   */
  const windowResizeHandler = () => {
    // Only recalculate if screen width changes
    if (lastWindowWidth.value === window.innerWidth) {
      return
    }

    lastWindowWidth.value = window.innerWidth

    if (activeDropdown.value) {
      rootEl.value?.classList.add('uia-no-transition')
      resizeDropdown()
      return
    }

    // Don't do unnecessary actions
    if (!backgroundEl.value?.getAttribute('style')) {
      return
    }

    // Prevent animations during resize
    rootEl.value?.classList.add('uia-no-transition', 'uia-no-transition_dropdown')
    setTimeout(() => rootEl.value?.classList.remove('uia-no-transition_dropdown'))

    closeDropdown()
    clearAllStyles()
  }

  const documentTouchMoveHandler = () => {
    isDragging.value = true
  }

  const documentTouchStartHandler = () => {
    isDragging.value = false
  }

  const documentEventEndHandler = () => {
    if (!isDragging.value) {
      closeDropdown()
    }
  }

  /**
   * Lifecycle
   */
  const registerGlobalListeners = () => {
    if (typeof window === 'undefined') return

    window.addEventListener('resize', windowResizeHandler)
    document.addEventListener('touchmove', documentTouchMoveHandler)
    document.addEventListener('touchstart', documentTouchStartHandler)
    document.body.addEventListener(pointerEvent.end, documentEventEndHandler)
  }

  const removeGlobalListeners = () => {
    if (typeof window === 'undefined') return

    window.removeEventListener('resize', windowResizeHandler)
    document.removeEventListener('touchmove', documentTouchMoveHandler)
    document.removeEventListener('touchstart', documentTouchStartHandler)
    document.body.removeEventListener(pointerEvent.end, documentEventEndHandler)
  }

  onMounted(() => {
    // Detect pointer events on client side
    if (typeof window !== 'undefined') {
      pointerEvent = window.PointerEvent
        ? {
            end: 'pointerup',
            enter: 'pointerenter',
            leave: 'pointerleave'
          }
        : {
            end: 'touchend',
            enter: 'mouseenter',
            leave: 'mouseleave'
          }
    }

    registerGlobalListeners()
  })

  onUnmounted(() => {
    removeGlobalListeners()
    clearCloseDropdownTimeout()
    clearEnableTransitionTimeout()
    clearDisableTransitionTimeout()
  })

  return {
    // State
    activeDropdown,
    activeDropdownItem,
    elementsWithDropdown,
    dropdownContainerItems,

    // Refs
    rootEl,
    linkContainerEl,
    dropdownContainerEl,
    backgroundEl,
    backgroundAltEl,
    arrowEl,

    // Methods
    openDropdown,
    closeDropdown,
    toggleDropdown,
    resizeDropdown,
    updateDataElements,
    registerDropdownElementsEvents,
    registerDropdownContainerEvents
  }
}
